import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Loader2, UserPlus, Users, Pencil } from "lucide-react";
import { toast } from "sonner";
import TeamMemberDialog from "@/components/TeamMemberDialog";
import ResetPasswordDialog from "@/components/ResetPasswordDialog";

export default function Team() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [members, setMembers] = useState(null);
  const [lots, setLots] = useState([]);
  const [editing, setEditing] = useState(undefined);

  const load = useCallback(() => api.get("/team").then(({ data }) => setMembers(data)), []);
  useEffect(() => {
    load();
    api.get("/team/lots").then(({ data }) => setLots(data));
  }, [load]);

  const lotName = (id) => lots.find((l) => l.id === id)?.name || "—";
  const canManage = (m) => m.id !== user?.id && m.role !== "owner" && (isOwner || m.role === "attendant");

  const toggleActive = async (m) => {
    try {
      await api.patch(`/team/${m.id}`, { active: !m.active });
      toast.success(m.active ? `${m.name} deactivated — login blocked` : `${m.name} reactivated`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="px-4 py-6 md:px-8">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-slate-800"><Users className="h-6 w-6 text-[#A0148C]" /></div>
          <div>
            <h1 className="font-heading text-3xl font-black tracking-tight text-slate-50">Team</h1>
            <p className="text-slate-400">{isOwner ? "Managers and attendants" : "Attendants"} — logins, roles and lot access.</p>
          </div>
        </div>
        <Button data-testid="team-add-user" onClick={() => setEditing(null)} className="h-11 gap-2 bg-[#A0148C] font-bold hover:bg-[#80106F]">
          <UserPlus className="h-4 w-4" /> Add employee
        </Button>
      </div>
      {!members ? <Loader2 className="mx-auto mt-16 h-8 w-8 animate-spin text-slate-500" /> : (
        <div className="space-y-3">
          {members.map((m) => (
            <Card key={m.id} data-testid={`team-user-${m.id}`} className={`flex flex-wrap items-center gap-4 border-slate-800 bg-slate-900 p-4 ${m.active ? "" : "opacity-60"}`}>
              <div className="min-w-[200px] flex-1">
                <p className="font-semibold text-slate-100">{m.name} {!m.active && <Badge className="ml-2 bg-red-600">Deactivated</Badge>}</p>
                <p className="font-data text-xs text-slate-400">{m.email}</p>
              </div>
              <Badge className={m.role === "owner" ? "bg-[#A0148C]" : m.role === "manager" ? "bg-blue-600" : "bg-slate-700"}>{m.role}</Badge>
              <p className="min-w-[160px] text-xs text-slate-400">
                {m.role === "attendant" ? (m.assignedLotIds?.length ? m.assignedLotIds.map(lotName).join(", ") : "No lots assigned") : "All lots"}
              </p>
              {canManage(m) && (
                <div className="flex items-center gap-2">
                  <Button data-testid={`team-edit-${m.id}`} variant="outline" onClick={() => setEditing(m)}
                    className="h-9 gap-1.5 border-slate-700 bg-slate-950 px-3 text-xs text-slate-200 hover:bg-slate-800"><Pencil className="h-3.5 w-3.5" /> Edit</Button>
                  <ResetPasswordDialog member={m} />
                  <label className="flex items-center gap-2 text-xs text-slate-400">
                    <Switch data-testid={`team-active-${m.id}`} checked={m.active} onCheckedChange={() => toggleActive(m)} /> Active
                  </label>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
      <TeamMemberDialog open={editing !== undefined} member={editing} onClose={() => setEditing(undefined)}
        lots={lots} isOwner={isOwner} onSaved={load} />
    </div>
  );
}
